import React, { useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import InputTimeSmall from "../../input/inputTimeSmall";
import { AddIcon, FilesIcon, TrashIcon } from "./../svg";
import useWindowSize from "../../../hooks/useWindowSize";
import moment from "moment";
import PopoverBtn from "../../popoverBtn";
import styled from "styled-components";

const MAX_DAY_ITEMS = 3;

const ScheduleDays = ({
  stateKey,
  addDayItem,
  applyAll,
  removeDayItem,
  resetDayField,
  setDayField,
  setToggleActiveDay,
  scheduleTitle,
  error,
}) => {
  const { locale, scheduleDays, lang, bookingLang, errorMsg } = useSelector(
    (state) => ({
      locale: state.changeLang.change,
      scheduleDays: stateKey
        ? state[stateKey].schedule.days
        : state.settings.schedule.days,
      lang: state.changeLang.language.restriction,
      bookingLang: state.changeLang.language.stepTwo.booking,
      errorMsg: state.changeLang.language.stepTwo.errors,
    })
  );
  moment.locale(locale);
  const dispatch = useDispatch();

  const weekDays = Array.from({ length: 7 }).map((_, i) =>
    moment().startOf("isoWeek").add(i, "days").format("dddd")
  );

  const { rootWidth } = useWindowSize();

  const handleSetActiveDay = (index) => {
    dispatch(setToggleActiveDay(index));
  };

  const [startErrors, setStartErrors] = useState({});
  const [endErrors, setEndErrors] = useState({});

  const handleAddField = (dayIndex) => {
    if (scheduleDays[dayIndex].length < MAX_DAY_ITEMS) {
      dispatch(addDayItem(dayIndex));
    }
  };

  const handleRemoveField = (dayIndex, fieldIndex) => {
    if (scheduleDays[dayIndex].length === 1) {
      dispatch(resetDayField(dayIndex));
    } else {
      dispatch(removeDayItem({ dayIndex, fieldIndex }));
    }
  };

  const handleApplyAll = () => {
    dispatch(applyAll());
  };

  const isEmptyScheduleError =
    error &&
    !scheduleDays.flat().some((el) => {
      const startDate = moment(el.dateStart, "HH:mm");
      const endDate = moment(el.dateEnd, "HH:mm");
      return (
        el.dateStart.length &&
        el.dateEnd.length &&
        startDate.isBefore(endDate) &&
        endDate.isAfter(startDate)
      );
    });

  return (
    <div className="position-relative">
      <div
        className={
          scheduleTitle
            ? `border rounded p-3 col-md-11${
                isEmptyScheduleError ? " border-danger error-field" : ""
              }`
            : ""
        }
      >
        <div className={`fw-bold mt-0 ${scheduleTitle ? "" : "mt-md-3"}`}>
          {scheduleTitle || lang[31]}
        </div>
        {scheduleDays.map((day, index) => (
          <Row
            xs="auto"
            className="d-flex align-items-start gx-3 gy-2 mt-3"
            key={index}
          >
            <Col xs={3}>
              <Form.Check
                type="checkbox"
                label={
                  rootWidth > 768
                    ? weekDays[index]
                    : weekDays[index].slice(0, 3)
                }
                id={index + 1}
                className="fw-bold fs-7 custom-check custom-check-small text-capitalize mt-2"
                checked={day[0].isActive}
                onChange={() => handleSetActiveDay(index)}
              />
            </Col>
            <Col xs={9} md={5} lg={9} xl={5} className="px-0">
              <Row className="gy-2">
                {day.map((item, i) => (
                  <Col key={`${index}${i}`} className="ps-0" xs={12}>
                    <Row className="align-items-center gx-1">
                      <Col xs={4}>
                        <InputTimeSmall
                          disabled={!item.isActive}
                          value={item.dateStart}
                          className={`w-100 p-2 ${
                            startErrors[`${index}-${i}`] ? "is-invalid" : ""
                          }`}
                          onChange={(e) => {
                            const startDate = moment(e.target.value, "HH:mm");
                            const endDate = moment(item.dateEnd, "HH:mm");

                            if (startDate.isAfter(endDate)) {
                              setStartErrors({
                                ...startErrors,
                                [`${index}-${i}`]: lang[41],
                              });
                            } else {
                              setStartErrors((prevErrors) => {
                                const newErrors = { ...prevErrors };
                                delete newErrors[`${index}-${i}`];
                                return newErrors;
                              });
                              dispatch(
                                setDayField({
                                  dayIndex: index,
                                  fieldIndex: i,
                                  valueIndex: "dateStart",
                                  value: e.target.value,
                                })
                              );
                            }
                          }}
                        />
                      </Col>
                      <Col xs={1}>
                        <div className="text-center">-</div>
                      </Col>
                      <Col xs={4}>
                        <InputTimeSmall
                          disabled={!item.isActive}
                          value={item.dateEnd}
                          className={`w-100 p-2 ${
                            endErrors[`${index}-${i}`] ? "is-invalid" : ""
                          }`}
                          onChange={(e) => {
                            const startDate = moment(item.dateStart, "HH:mm");
                            const endDate = moment(e.target.value, "HH:mm");

                            if (endDate.isBefore(startDate)) {
                              setEndErrors({
                                ...endErrors,
                                [`${index}-${i}`]: lang[40],
                              });
                            } else {
                              setEndErrors((prevErrors) => {
                                const newErrors = { ...prevErrors };
                                delete newErrors[`${index}-${i}`];
                                return newErrors;
                              });
                              dispatch(
                                setDayField({
                                  dayIndex: index,
                                  fieldIndex: i,
                                  valueIndex: "dateEnd",
                                  value: e.target.value,
                                })
                              );
                            }
                          }}
                        />
                      </Col>
                      <Col xs={3} className="d-flex justify-content-start">
                        <Button
                          variant="link"
                          className="me-3 -mt-4 ms-2 p-0"
                          onClick={() => handleRemoveField(index, i)}
                          disabled={!item.isActive}
                        >
                          <TrashIcon />
                        </Button>
                        {day.length - 1 === i && day.length < MAX_DAY_ITEMS && (
                          <Button
                            variant="link"
                            className="p-0"
                            onClick={() => handleAddField(index)}
                            disabled={!item.isActive}
                          >
                            <AddIcon />
                          </Button>
                        )}
                      </Col>
                      {startErrors[`${index}-${i}`] && (
                        <Col xs={12}>
                          <div className="text-danger">
                            {startErrors[`${index}-${i}`]}
                          </div>
                        </Col>
                      )}
                      {endErrors[`${index}-${i}`] && (
                        <Col xs={12}>
                          <div className="text-danger">
                            {endErrors[`${index}-${i}`]}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Col>
                ))}
              </Row>
            </Col>

            {index === 0 && (
              <Col xs={12} md={4} lg={12} xl={4}>
                <div
                  className="cursor-pointer text-primary fs-7 mt-2 pt-1 d-none d-md-block text-nowrap d-lg-none d-xl-block"
                  onClick={handleApplyAll}
                >
                  <FilesIcon className="me-2" /> {lang[32]}
                </div>
                <Button
                  variant="outline-primary"
                  className="cursor-pointer text-primary fs-7 mt-2 pt-1 w-100 d-md-none d-lg-block d-xl-none"
                  onClick={handleApplyAll}
                >
                  <FilesIcon className="me-2" /> {lang[32]}
                </Button>
              </Col>
            )}
          </Row>
        ))}
      </div>

      {isEmptyScheduleError && <div className="text-danger">{errorMsg[3]}</div>}

      {scheduleTitle && (
        <div className="position-absolute top-0 end-0 w-auto mt-3 d-flex flex-column align-items-end me-3">
          <PopoverBtn text={bookingLang[17]} />
          <StyledPopoverBtn className="mt-md-4 pt-md-2">
            <PopoverBtn text={bookingLang[18]} />
          </StyledPopoverBtn>
        </div>
      )}
    </div>
  );
};

export default ScheduleDays;

const StyledPopoverBtn = styled.div`
  & > div {
    @media (max-width: 767px) {
      margin-top: 85px;
      margin-right: 15px;
    }
  }
`;
